import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { ServiceOfferIds, getBaseUrl } from 'prefs-and-service-offers';
import { useHistory } from 'react-router-dom';
import { useBillingApprovalAreaStatus } from './billingApproval';
import { useContactsAreaStatus } from './contacts';
import { useTireAreaStatus } from './tire';
import { AreaHeader, Panel, StatusPill } from './components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.9rem 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    actionIcon: {
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 20,
    },
    actionText: {
      flex: 1,
      minWidth: 0,
    },
    actionTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    actionTitle: {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    actionTag: {
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.action.hover,
      padding: '2px 7px',
      borderRadius: 5,
    },
    actionDescription: {
      marginTop: 2,
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
    actionButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flexShrink: 0,
    },
    fixButton: {
      textTransform: 'none',
    },
    legacyLink: {
      fontSize: '0.8rem',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    sectionLabel: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      margin: '1.5rem 0 0.75rem',
    },
    readyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '0.75rem',
    },
    readyCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.85rem 1rem',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
    },
    readyCheck: {
      flexShrink: 0,
      width: 26,
      height: 26,
      borderRadius: 6,
      backgroundColor: '#e6f4ea',
      color: '#1e7e34',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 15,
      fontWeight: 700,
    },
    readyTitle: {
      fontSize: '0.85rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    readySubtitle: {
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
    },
  }),
);

interface ActionItem {
  icon: string;
  iconColor: string;
  title: string;
  tag: string;
  description: string;
  actionLabel: string;
  actionDisabled?: boolean;
  onActionClick?: () => void;
  onLegacyClick?: () => void;
}

interface ReadyItem {
  title: string;
  subtitle: string;
}

export function OverviewSubArea() {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const permissions = usePermissions();
  const { incompleteCount } = useContactsAreaStatus();
  const { baseMissingReasons } = useTireAreaStatus();
  const { incompleteCount: billingIncompleteCount } = useBillingApprovalAreaStatus();

  const urbanOnlyFleet = !!(
    permissions.location &&
    permissions.location.is_urban &&
    !permissions.location.is_commercial &&
    !permissions.location.override_is_commercial
  );
  const legacyServiceOffer = urbanOnlyFleet ? ServiceOfferIds.bulkOrder : ServiceOfferIds.onCall;
  const legacyBillingPath = `${getBaseUrl(false)}/${legacyServiceOffer}/billings/profiles`;

  const canCreateTire = permissions.allowsAction('tires.create');
  const canCreateContact = permissions.allowsAction('contacts.create');
  const canReadBilling = permissions.allowsAction('billing.read');

  const actionItems: ActionItem[] = React.useMemo(() => {
    const items: ActionItem[] = [];

    if (baseMissingReasons.length > 0) {
      items.push({
        icon: '\u{1F6DE}',
        iconColor: '#fff8e1',
        title: t('Tire preferences need attention'),
        tag: t('Tire'),
        description: baseMissingReasons.join(' '),
        actionLabel: t('Add profile'),
        actionDisabled: !canCreateTire,
        onActionClick: canCreateTire ? () => history.push(`${getBaseUrl(false)}/tire-profile/create`) : undefined,
        onLegacyClick: () => history.push(`${getBaseUrl(false)}/tire-profile`),
      });
    }

    if (incompleteCount > 0) {
      items.push({
        icon: '\u{1F4DE}',
        iconColor: '#fdecea',
        title: t('Contacts need completion'),
        tag: t('Contacts'),
        description: t('{{count}} contact role(s) are missing required details.', { count: incompleteCount }),
        actionLabel: t('Add contact'),
        actionDisabled: !canCreateContact,
        onActionClick: canCreateContact ? () => history.push(`${getBaseUrl(false)}/contacts/create`) : undefined,
        onLegacyClick: () => history.push(`${getBaseUrl(false)}/contacts`),
      });
    }

    if (billingIncompleteCount > 0) {
      items.push({
        icon: '\u{1F4B0}',
        iconColor: '#fdecea',
        title: t('Billing profiles need updates'),
        tag: t('Billing'),
        description: t('{{count}} billing issue(s) were found across Tire and Mechanical sections.', {
          count: billingIncompleteCount,
        }),
        actionLabel: t('Review profiles'),
        actionDisabled: !canReadBilling,
        onActionClick: canReadBilling ? () => history.push(legacyBillingPath) : undefined,
        onLegacyClick: () => history.push(legacyBillingPath),
      });
    }

    return items;
  }, [
    baseMissingReasons,
    billingIncompleteCount,
    canCreateContact,
    canCreateTire,
    canReadBilling,
    history,
    incompleteCount,
    legacyBillingPath,
    t,
  ]);

  const readyItems: ReadyItem[] = React.useMemo(() => {
    const items: ReadyItem[] = [];
    if (baseMissingReasons.length === 0) {
      items.push({
        title: t('Tire preferences are complete'),
        subtitle: t('All correlated tire profile checks passed.'),
      });
    }
    if (incompleteCount === 0) {
      items.push({
        title: t('Contacts are complete'),
        subtitle: t('Primary role contacts are fully configured.'),
      });
    }
    if (billingIncompleteCount === 0) {
      items.push({
        title: t('Billing profiles are complete'),
        subtitle: t('Tire and Mechanical billing profiles have the expected limits configured.'),
      });
    }
    if (items.length === 0) {
      items.push({
        title: t('No areas are fully complete yet'),
        subtitle: t('Resolve the action-needed items above to move preferences into ready state.'),
      });
    }
    return items;
  }, [baseMissingReasons.length, billingIncompleteCount, incompleteCount, t]);

  return (
    <>
      <AreaHeader
        icon={<span aria-hidden>&#9776;</span>}
        iconColor="#0b2e59"
        title={t('Overview')}
        subtitle={t("A running checklist of your key preferences - what's set, what's missing, and why it matters.")}
      />

      <Panel
        title={t('Action needed - correlated settings are missing')}
        action={<StatusPill label={`${actionItems.length}`} tone={actionItems.length > 0 ? 'danger' : 'success'} showDot={false} />}
      >
        {actionItems.length > 0 ? (
          actionItems.map((item) => (
            <div className={classes.actionRow} key={item.title}>
              <div className={classes.actionIcon} style={{ backgroundColor: item.iconColor }}>
                <span aria-hidden>{item.icon}</span>
              </div>
              <div className={classes.actionText}>
                <div className={classes.actionTitleRow}>
                  <Typography className={classes.actionTitle}>{item.title}</Typography>
                  <span className={classes.actionTag}>{item.tag}</span>
                </div>
                <Typography className={classes.actionDescription}>{item.description}</Typography>
              </div>
              <div className={classes.actionButtons}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.fixButton}
                  disabled={item.actionDisabled}
                  onClick={item.onActionClick}
                >
                  {item.actionLabel} &rarr;
                </Button>
                <span className={classes.legacyLink} onClick={item.onLegacyClick}>
                  {t('Open in legacy')}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className={classes.actionRow}>
            <div className={classes.actionIcon} style={{ backgroundColor: '#e6f4ea' }}>
              <span aria-hidden>&#10003;</span>
            </div>
            <div className={classes.actionText}>
              <Typography className={classes.actionTitle}>{t('No correlated issues detected')}</Typography>
              <Typography className={classes.actionDescription}>
                {t('Your tire, contact, and billing checks are currently passing for this account.')}
              </Typography>
            </div>
          </div>
        )}
      </Panel>

      <Typography className={classes.sectionLabel}>{t('Set & Ready')}</Typography>
      <div className={classes.readyGrid}>
        {readyItems.map((item) => (
          <div className={classes.readyCard} key={item.title}>
            <span className={classes.readyCheck} aria-hidden>
              &#10003;
            </span>
            <div>
              <Typography className={classes.readyTitle}>{item.title}</Typography>
              <Typography className={classes.readySubtitle}>{item.subtitle}</Typography>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default OverviewSubArea;
